import { z } from 'zod';

/**
 * Site kinds and formats are seeded lookup rows (migrations 202610081500 and, for `property`,
 * 202610200100); these mirror them. `property` sites are non-trading premises (stores, yards)
 * that only Property uses: no staff, rota, trading days or attendance.
 */
export const SiteKindSchema = z.enum([
  'branch',
  'distribution_centre',
  'head_office',
  'field',
  'property',
]);

/** Site kinds that never trade or hold staff, left out of rota, calendar, attendance and absence. */
export const NON_TRADING_SITE_KINDS = ['property'] as const satisfies readonly z.infer<
  typeof SiteKindSchema
>[];
export const SiteFormatSchema = z.enum(['trade', 'retail']);
export const SiteStatusSchema = z.enum(['open', 'closed', 'opening']);
export const OrgStatusSchema = z.enum(['active', 'closed']);

export const PositionKindSchema = z.enum([
  'bm',
  'abm',
  'rm',
  'md',
  'director',
  'hr_lead',
  'fleet_lead',
  'other',
]);

/** Who currently sits in a seat (acting or substantive). */
export const PositionHolderSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  initials: z.string(),
  since: z.iso.date(),
  isActing: z.boolean(),
});

export const PositionSummarySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  title: z.string(),
  kind: PositionKindSchema,
  roleMailbox: z.string().nullable(),
  holder: PositionHolderSchema.nullable(),
});

export const SiteSummarySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  kind: SiteKindSchema,
  format: SiteFormatSchema,
  status: SiteStatusSchema,
  regionCode: z.string(),
  divisionCode: z.string(),
  positions: z.array(PositionSummarySchema),
});

export const SiteDetailSchema = SiteSummarySchema.extend({
  regionName: z.string(),
  divisionName: z.string(),
  address: z.string().nullable(),
  postcode: z.string().nullable(),
  phone: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  openedOn: z.iso.date().nullable(),
  closedOn: z.iso.date().nullable(),
  sharedPcs: z.boolean(),
  /** Public IP ranges that count as "at this site". Collected in M2; empty until then. */
  networkCidrs: z.array(z.string()),
  hours: z.record(z.string(), z.unknown()).nullable(),
  /** Headcount of active people whose home site this is. */
  activePeople: z.number().int().nonnegative(),
});

/**
 * Organisation tree: divisions → regions → sites. Because a region can span two divisions
 * (South Wales does), a region appears under every division that has sites in it, each time
 * with only that division's sites. Region-level positions (the RM seat) are repeated on each.
 */
export const RegionNodeSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  status: OrgStatusSchema,
  positions: z.array(PositionSummarySchema),
  sites: z.array(SiteSummarySchema),
});

export const DivisionNodeSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  status: OrgStatusSchema,
  /** Seeded placeholder names ("Division 1") carry a note that they can be renamed. */
  notes: z.string().nullable(),
  positions: z.array(PositionSummarySchema),
  regions: z.array(RegionNodeSchema),
});

export const OrgTreeSchema = z.object({
  divisions: z.array(DivisionNodeSchema),
  totals: z.object({
    divisions: z.number().int(),
    regions: z.number().int(),
    sites: z.number().int(),
    positions: z.number().int(),
    vacantPositions: z.number().int(),
  }),
});

export type SiteKind = z.infer<typeof SiteKindSchema>;
export type SiteFormat = z.infer<typeof SiteFormatSchema>;
export type SiteStatus = z.infer<typeof SiteStatusSchema>;
export type PositionKind = z.infer<typeof PositionKindSchema>;
export type PositionHolder = z.infer<typeof PositionHolderSchema>;
export type PositionSummary = z.infer<typeof PositionSummarySchema>;
export type SiteSummary = z.infer<typeof SiteSummarySchema>;
export type SiteDetail = z.infer<typeof SiteDetailSchema>;
export type RegionNode = z.infer<typeof RegionNodeSchema>;
export type DivisionNode = z.infer<typeof DivisionNodeSchema>;
export type OrgTree = z.infer<typeof OrgTreeSchema>;
